import React, { useMemo, useState } from 'react';
import { Card, Tab, Tabs } from 'react-bootstrap';
import { BarChart3 } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Icon from './Icon.jsx';

const CHART_COLORS = ['#0f766e', '#2563eb', '#dc2626', '#d97706', '#7c3aed', '#0891b2', '#65a30d', '#db2777'];
const CHART_MODE = {
  LINE: 'line',
  STACKED_BAR: 'stacked-bar',
};

const formatChartValue = (value, unit) => `${Number(value || 0).toFixed(1)} ${unit}`;

const chartTooltipStyle = {
  backgroundColor: 'var(--bg-primary)',
  border: '1px solid var(--border-light)',
  borderRadius: '12px',
};

const getTooltipEntryMeta = (entry) => entry?.payload?.[`${entry.dataKey}Meta`] || null;

const buildRankedStackedBarData = (rows = [], teams = [], getDataKey) => {
  const slotKeys = teams.map((_, index) => `stackSlot${index}`);

  const data = rows.map((row) => {
    const rankedEntries = teams
      .map((team) => ({
        name: team.name,
        color: team.color,
        originalKey: getDataKey(team),
        value: Number(row[getDataKey(team)] || 0),
      }))
      .filter((entry) => entry.value > 0)
      .sort((left, right) => {
        const valueDifference = left.value - right.value;
        if (valueDifference !== 0) {
          return valueDifference;
        }

        return left.name.localeCompare(right.name);
      });

    const stackedRow = {
      week: row.week,
      weekNumber: row.weekNumber,
    };

    slotKeys.forEach((slotKey, index) => {
      const entry = rankedEntries[index];
      stackedRow[slotKey] = Number(entry?.value || 0);
      stackedRow[`${slotKey}Meta`] = entry || null;
    });

    return stackedRow;
  });

  return { data, slotKeys };
};

const buildSortedTooltipEntries = (payload = [], hoveredDataKey = null, onlyHoveredSeries = false) => {
  let entries = Array.isArray(payload)
    ? payload.filter((entry) => entry && entry.dataKey && entry.value != null)
    : [];

  if (onlyHoveredSeries) {
    if (!hoveredDataKey) {
      return [];
    }

    entries = entries.filter((entry) => entry.dataKey === hoveredDataKey);
  }

  return [...entries]
    .map((entry) => {
      const meta = getTooltipEntryMeta(entry);
      if (!meta) {
        return entry;
      }

      return {
        ...entry,
        name: meta.name,
        color: meta.color,
        stroke: meta.color,
        value: meta.value,
        originalDataKey: meta.originalKey,
      };
    })
    .sort((left, right) => {
    const valueDifference = Number(right.value || 0) - Number(left.value || 0);
    if (valueDifference !== 0) {
      return valueDifference;
    }

    return String(left.name || '').localeCompare(String(right.name || ''));
    });
};

const CustomChartTooltip = ({ active, label, payload, hoveredDataKey, onlyHoveredSeries = false, unit = 'mi' }) => {
  if (!active) {
    return null;
  }

  const entries = buildSortedTooltipEntries(payload, hoveredDataKey, onlyHoveredSeries);
  if (!entries.length) {
    return null;
  }

  return (
    <div className="league-chart-tooltip" style={chartTooltipStyle}>
      <div className="league-chart-tooltip-label">{label}</div>
      <div className="league-chart-tooltip-list">
        {entries.map((entry) => (
          <div key={String(entry.dataKey)} className="league-chart-tooltip-row">
            <span className="league-chart-tooltip-name" style={{ color: entry.color || entry.stroke || 'var(--text-primary)' }}>
              {entry.name}
            </span>
            <span className="league-chart-tooltip-value">{formatChartValue(entry.value, unit)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const TeamStandingsChart = ({ teams = [], isSeasonInactive = false, mode = 'cumulative' }) => {
  const [teamChartMode, setTeamChartMode] = useState(CHART_MODE.LINE);
  const [ownerChartMode, setOwnerChartMode] = useState(CHART_MODE.LINE);
  const [hoveredTeamSeriesKey, setHoveredTeamSeriesKey] = useState(null);
  const [hoveredOwnerSeriesKey, setHoveredOwnerSeriesKey] = useState(null);
  const isHeadToHeadMode = mode === 'head_to_head';
  const valueUnit = isHeadToHeadMode ? 'pts' : 'mi';
  const primarySeriesLabel = isHeadToHeadMode ? 'Matchup Points' : 'Team Mileage';
  const secondarySeriesLabel = isHeadToHeadMode ? 'Owner Bonus Points' : 'Owner Bonus Miles';
  const chartTitle = isHeadToHeadMode ? 'Weekly Team Points' : 'Team Performance by Week';
  const chartSubtitle = isHeadToHeadMode
    ? 'Track weekly matchup scoring and owner bonus points across the season.'
    : 'Switch between total team mileage and owner bonus miles for the current season.';
  const emptyPrimaryMessage = isHeadToHeadMode
    ? 'No weekly matchup points are available yet.'
    : 'No weekly team mileage is available yet.';
  const emptySecondaryMessage = isHeadToHeadMode
    ? 'No weekly owner bonus points are available yet.'
    : 'No owner bonus miles are available yet.';
  const getSeriesValue = (entry) => Number(entry?.points ?? entry?.miles ?? entry?.value ?? 0);

  const chartTeams = useMemo(() => (
    teams
      .filter((team) => {
        const primarySeries = isHeadToHeadMode ? team.weeklyPoints : team.weeklyMileage;
        const secondarySeries = isHeadToHeadMode ? team.weeklyOwnerBonusPoints : team.ownerBonusMileage;
        const hasPrimarySeries = Array.isArray(primarySeries) && primarySeries.length > 0;
        const hasSecondarySeries = Array.isArray(secondarySeries) && secondarySeries.length > 0;
        return hasPrimarySeries || hasSecondarySeries;
      })
      .map((team, index) => ({
        key: String(team._id || team.teamId || team.name || `team-${index}`),
        ownerBonusKey: `${String(team._id || team.teamId || team.name || `team-${index}`)}-owner-bonus`,
        name: team.name || team.teamName || 'Unnamed team',
        color: CHART_COLORS[index % CHART_COLORS.length],
        primarySeries: Array.isArray(isHeadToHeadMode ? team.weeklyPoints : team.weeklyMileage)
          ? (isHeadToHeadMode ? team.weeklyPoints : team.weeklyMileage)
          : [],
        secondarySeries: Array.isArray(isHeadToHeadMode ? team.weeklyOwnerBonusPoints : team.ownerBonusMileage)
          ? (isHeadToHeadMode ? team.weeklyOwnerBonusPoints : team.ownerBonusMileage)
          : [],
      }))
      .sort((left, right) => left.name.localeCompare(right.name))
  ), [isHeadToHeadMode, teams]);

  const chartData = useMemo(() => {
    const weekNumbers = new Set();

    for (const team of chartTeams) {
      for (const entry of team.primarySeries) {
        if (entry?.weekNumber != null) {
          weekNumbers.add(Number(entry.weekNumber));
        }
      }

      for (const entry of team.secondarySeries) {
        if (entry?.weekNumber != null) {
          weekNumbers.add(Number(entry.weekNumber));
        }
      }
    }

    return Array.from(weekNumbers)
      .sort((left, right) => left - right)
      .map((weekNumber) => {
        const row = {
          week: `Week ${weekNumber}`,
          weekNumber,
        };

        for (const team of chartTeams) {
          const matchingWeek = team.primarySeries.find((entry) => Number(entry.weekNumber) === weekNumber);
          const matchingOwnerBonusWeek = team.secondarySeries.find((entry) => Number(entry.weekNumber) === weekNumber);
          row[team.key] = getSeriesValue(matchingWeek);
          row[team.ownerBonusKey] = getSeriesValue(matchingOwnerBonusWeek);
        }

        return row;
      });
  }, [chartTeams]);

  const teamMileageChartData = useMemo(() => (
    chartData.filter((row) => chartTeams.some((team) => Number(row[team.key] || 0) > 0))
  ), [chartData, chartTeams]);

  const ownerBonusChartData = useMemo(() => (
    chartData.filter((row) => chartTeams.some((team) => Number(row[team.ownerBonusKey] || 0) > 0))
  ), [chartData, chartTeams]);

  const hasChartData = chartData.length > 0;
  const hasTeamMileage = teamMileageChartData.length > 0;
  const hasOwnerBonusMileage = ownerBonusChartData.length > 0;
  const teamMileageStackedChart = useMemo(
    () => buildRankedStackedBarData(teamMileageChartData, chartTeams, (team) => team.key),
    [teamMileageChartData, chartTeams]
  );
  const ownerBonusStackedChart = useMemo(
    () => buildRankedStackedBarData(ownerBonusChartData, chartTeams, (team) => team.ownerBonusKey),
    [ownerBonusChartData, chartTeams]
  );

  return (
    <Card className="modern-card mb-5">
      <div className="modern-card-header">
        <h2 className="modern-card-title"><Icon icon={BarChart3} size={18} className="me-2 align-middle" />{chartTitle}</h2>
        <p className="modern-card-subtitle">{chartSubtitle}</p>
      </div>

      <div className="p-3">
        {hasChartData ? (
          <>
            <div className="league-chart-legend mb-3">
              {chartTeams.map((team) => (
                <div key={team.key} className="league-chart-legend-item">
                  <span className="league-chart-legend-swatch" style={{ backgroundColor: team.color }} aria-hidden="true" />
                  <span>{team.name}</span>
                </div>
              ))}
            </div>
            <Tabs defaultActiveKey="team-mileage" className="league-chart-tabs mb-3">
              <Tab eventKey="team-mileage" title={primarySeriesLabel}>
                <div className="league-chart-panel pt-3">
                  {hasTeamMileage ? (
                    <>
                      <div className="league-chart-controls mb-3">
                        <button
                          type="button"
                          className={`league-chart-toggle ${teamChartMode === CHART_MODE.LINE ? 'active' : ''}`}
                          onClick={() => setTeamChartMode(CHART_MODE.LINE)}
                        >
                          Line
                        </button>
                        <button
                          type="button"
                          className={`league-chart-toggle ${teamChartMode === CHART_MODE.STACKED_BAR ? 'active' : ''}`}
                          onClick={() => setTeamChartMode(CHART_MODE.STACKED_BAR)}
                        >
                          Stacked Bar
                        </button>
                      </div>
                      <div className="standings-chart-container" style={{ height: 320 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          {teamChartMode === CHART_MODE.LINE ? (
                            <LineChart
                              data={teamMileageChartData}
                              margin={{ top: 12, right: 16, left: 0, bottom: 0 }}
                              onMouseLeave={() => setHoveredTeamSeriesKey(null)}
                            >
                              <CartesianGrid stroke="var(--border-light)" strokeDasharray="3 3" />
                              <XAxis dataKey="week" tickLine={false} axisLine={false} />
                              <YAxis tickLine={false} axisLine={false} width={56} tickFormatter={(value) => `${value}`} />
                              <Tooltip
                                cursor={false}
                                content={
                                  <CustomChartTooltip
                                    hoveredDataKey={hoveredTeamSeriesKey}
                                    onlyHoveredSeries
                                    unit={valueUnit}
                                  />
                                }
                              />
                              {chartTeams.map((team) => (
                                <Line
                                  key={team.key}
                                  type="monotone"
                                  dataKey={team.key}
                                  name={team.name}
                                  stroke={team.color}
                                  strokeWidth={3}
                                  dot={{ r: 3 }}
                                  activeDot={{
                                    r: 5,
                                    onMouseEnter: () => setHoveredTeamSeriesKey(team.key),
                                    onMouseLeave: () => setHoveredTeamSeriesKey(null),
                                  }}
                                />
                              ))}
                            </LineChart>
                          ) : (
                            <BarChart data={teamMileageStackedChart.data} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                              <CartesianGrid stroke="var(--border-light)" strokeDasharray="3 3" />
                              <XAxis dataKey="week" tickLine={false} axisLine={false} />
                              <YAxis tickLine={false} axisLine={false} width={56} tickFormatter={(value) => `${value}`} />
                              <Tooltip content={<CustomChartTooltip unit={valueUnit} />} />
                              {teamMileageStackedChart.slotKeys.map((slotKey) => (
                                <Bar
                                  key={slotKey}
                                  dataKey={slotKey}
                                  name={slotKey}
                                  stackId="team-mileage"
                                  fill="transparent"
                                >
                                  {teamMileageStackedChart.data.map((row) => (
                                    <Cell
                                      key={`${row.weekNumber}-${slotKey}`}
                                      fill={row[`${slotKey}Meta`]?.color || 'transparent'}
                                    />
                                  ))}
                                </Bar>
                              ))}
                            </BarChart>
                          )}
                        </ResponsiveContainer>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-5">
                      <p className="text-secondary mb-0">{emptyPrimaryMessage}</p>
                    </div>
                  )}
                </div>
              </Tab>
              <Tab eventKey="owner-bonus" title={secondarySeriesLabel}>
                <div className="league-chart-panel pt-3">
                  {hasOwnerBonusMileage ? (
                    <>
                      <div className="league-chart-controls mb-3">
                        <button
                          type="button"
                          className={`league-chart-toggle ${ownerChartMode === CHART_MODE.LINE ? 'active' : ''}`}
                          onClick={() => setOwnerChartMode(CHART_MODE.LINE)}
                        >
                          Line
                        </button>
                        <button
                          type="button"
                          className={`league-chart-toggle ${ownerChartMode === CHART_MODE.STACKED_BAR ? 'active' : ''}`}
                          onClick={() => setOwnerChartMode(CHART_MODE.STACKED_BAR)}
                        >
                          Stacked Bar
                        </button>
                      </div>
                      <div className="standings-chart-container" style={{ height: 320 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          {ownerChartMode === CHART_MODE.LINE ? (
                            <LineChart
                              data={ownerBonusChartData}
                              margin={{ top: 12, right: 16, left: 0, bottom: 0 }}
                              onMouseLeave={() => setHoveredOwnerSeriesKey(null)}
                            >
                              <CartesianGrid stroke="var(--border-light)" strokeDasharray="3 3" />
                              <XAxis dataKey="week" tickLine={false} axisLine={false} />
                              <YAxis tickLine={false} axisLine={false} width={56} tickFormatter={(value) => `${value}`} />
                              <Tooltip
                                cursor={false}
                                content={
                                  <CustomChartTooltip
                                    hoveredDataKey={hoveredOwnerSeriesKey}
                                    onlyHoveredSeries
                                    unit={valueUnit}
                                  />
                                }
                              />
                              {chartTeams.map((team) => (
                                <Line
                                  key={team.ownerBonusKey}
                                  type="monotone"
                                  dataKey={team.ownerBonusKey}
                                  name={team.name}
                                  stroke={team.color}
                                  strokeDasharray="6 6"
                                  strokeWidth={3}
                                  dot={{ r: 3 }}
                                  activeDot={{
                                    r: 5,
                                    onMouseEnter: () => setHoveredOwnerSeriesKey(team.ownerBonusKey),
                                    onMouseLeave: () => setHoveredOwnerSeriesKey(null),
                                  }}
                                  connectNulls
                                />
                              ))}
                            </LineChart>
                          ) : (
                            <BarChart data={ownerBonusStackedChart.data} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                              <CartesianGrid stroke="var(--border-light)" strokeDasharray="3 3" />
                              <XAxis dataKey="week" tickLine={false} axisLine={false} />
                              <YAxis tickLine={false} axisLine={false} width={56} tickFormatter={(value) => `${value}`} />
                              <Tooltip content={<CustomChartTooltip unit={valueUnit} />} />
                              {ownerBonusStackedChart.slotKeys.map((slotKey) => (
                                <Bar
                                  key={slotKey}
                                  dataKey={slotKey}
                                  name={slotKey}
                                  stackId="owner-bonus"
                                  fill="transparent"
                                >
                                  {ownerBonusStackedChart.data.map((row) => (
                                    <Cell
                                      key={`${row.weekNumber}-${slotKey}`}
                                      fill={row[`${slotKey}Meta`]?.color || 'transparent'}
                                    />
                                  ))}
                                </Bar>
                              ))}
                            </BarChart>
                          )}
                        </ResponsiveContainer>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-5">
                      <p className="text-secondary mb-0">{emptySecondaryMessage}</p>
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>
          </>
        ) : (
          <div className="text-center py-5">
            <div className="text-secondary mb-3 d-flex justify-content-center"><Icon icon={BarChart3} size={28} /></div>
            <h5 className="text-secondary">{isSeasonInactive ? 'Season Not Started' : isHeadToHeadMode ? 'No Weekly Matchup Scores Yet' : 'No Weekly Mileage Yet'}</h5>
            <p className="text-secondary mb-0">
              {isSeasonInactive
                ? (isHeadToHeadMode
                  ? 'Weekly matchup points will appear here once the season begins.'
                  : 'Weekly team mileage will appear here once the season begins.')
                : (isHeadToHeadMode
                  ? 'Weekly matchup points will appear here once completed matchups have scores.'
                  : 'Weekly team mileage will appear here once teams log season activities.')}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default TeamStandingsChart;
